import type { Locale } from 'date-fns';
import { format, isAfter } from 'date-fns';

import { Option, UserProps } from 'types';

type DateArg = string | number | Date | null | undefined;

export const safeFormatDate = (
  date: DateArg,
  formatString: string,
  options?: {
    locale?: Locale;
    weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
    firstWeekContainsDate?: number;
    useAdditionalWeekYearTokens?: boolean;
    useAdditionalDayOfYearTokens?: boolean;
  },
) => {
  if (!date) {
    return ' -- ';
  }
  let safeDate = date;
  if (typeof date === 'string') {
    safeDate = new Date(date);
  }
  if (safeDate.toString() === 'Invalid Date') {
    return ' -- ';
  }
  return format(safeDate as Date | number, formatString, options);
};

export const isISO8601 = (dateTime: string) => {
  const re =
    //eslint-disable-next-line
    /^([\+-]?\d{4}(?!\d{2}\b))((-?)((0[1-9]|1[0-2])(\3([12]\d|0[1-9]|3[01]))?|W([0-4]\d|5[0-2])(-?[1-7])?|(00[1-9]|0[1-9]\d|[12]\d{2}|3([0-5]\d|6[1-6])))([T\s]((([01]\d|2[0-3])((:?)[0-5]\d)?|24\:?00)([\.,]\d+(?!:))?)?(\17[0-5]\d([\.,]\d+)?)?([zZ]|([\+-])([01]\d|2[0-3]):?([0-5]\d)?)?)?)?$/;
  return re.test(dateTime);
};

export const convertUTCDateTimeToDateTime = (dateTime: string) => {
  if (!isISO8601(dateTime)) {
    return '--';
  }
  const utcDateTime = `${dateTime}Z`;
  return convertToDateTimeString(new Date(utcDateTime).toLocaleString());
};
export const convertToDateTimeString = (date: DateArg) =>
  safeFormatDate(date, 'E, MMM d, yyyy, h:mm a');

export const convertToDateString = (dateString: string) => {
  const date = new Date(dateString);
  if (date.toString() === 'Invalid Date') {
    return ' -- ';
  }

  return format(date, 'E, MMM d, yyyy');
};
export const convertToDateTimeShort = (date: DateArg) =>
  safeFormatDate(date, 'dd/MM/yyyy, h:mm a');

export const convertToDateShort = (date: DateArg) =>
  safeFormatDate(date, 'dd/MM/yyyy');

export const convertToTime = (date: DateArg) => safeFormatDate(date, 'h:mm a');

const isoDateFormat = /^\d{4}-\d{2}-\d{2}(?:\.\d*)?$/;
export const isIsoDateString = (value: any): boolean => {
  return value && typeof value === 'string' && isoDateFormat.test(value);
};

export const ensureDate = (date: Date | string | number) => {
  return typeof date === 'string' || typeof date === 'number'
    ? new Date(date)
    : date;
};
export const dateStringSort = (dateStringA: DateArg, dateStringB: DateArg) => {
  if (!dateStringA && !dateStringB) {
    return 0;
  }
  if (!dateStringA) {
    return 1;
  }
  if (!dateStringB) {
    return -1;
  }
  const dateA = ensureDate(dateStringA);
  const dateB = ensureDate(dateStringB);
  const isAAfterB = isAfter(dateA, dateB);
  return isAAfterB ? -1 : 1;
};

export const convertNumToBoolean = (int: number) => {
  return int === 0 ? false : true;
};

export const convertBoolToString = (option?: boolean | null) => {
  return option === null || option === undefined ? '' : option.toString();
};

export const convertStringToBool = (option: string | undefined | null) => {
  if (option === 'false') {
    return false;
  }
  if (option === 'true') {
    return true;
  }

  return null;
};

export const parsedContractID = (contractId: string) => {
  const parsedString = contractId.replace(/-| /gi, '');
  return parsedString;
};

export const cleanArrayString = (input: string) => {
  return input ? input.replace(/[[\]'"]/g, '') : '';
};

export const getLabel = (props: { value: string; options: Array<Option> }) => {
  const { value, options } = props;
  const option = options.find((option) => option.value === value);
  return option ? option.label : '--';
};

export const getFullUserNameOrId = (user: UserProps) => {
  const { first_name, last_name, id } = user;

  if (first_name && last_name) {
    return `${first_name} ${last_name}`;
  }
  return `User ${id}`;
};

export const getDelta = (props: {
  magnitude: number | undefined;
  change: number | undefined;
}) => {
  const { magnitude, change } = props;
  if (typeof magnitude !== 'number' || typeof change !== 'number') {
    return '--';
  }
  const perc = change / magnitude;
  return perc.toFixed(2) + '%';
};

//takes: a float representing an amount out of 1
//returns: string representing that float as a percent
export const formatPercent = (float: number) => {
  return Number(float).toLocaleString(undefined, {
    style: 'percent',
    maximumFractionDigits: 2,
  });
};
